if (!customElements.get('drbo-cart-recommendations')) {
  customElements.define('drbo-cart-recommendations', class extends HTMLElement {
    connectedCallback() {
      this.refresh = this.refresh.bind(this);
      this.scheduleRefresh = () => {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = setTimeout(this.refresh, 350);
      };
      this.cartObserver = new MutationObserver(this.scheduleRefresh);
      this.grid = this.querySelector('[data-cart-recommendation-grid]');
      this.events = new AbortController();
      this.updateControls = this.updateControls.bind(this);
      this.grid.addEventListener('scroll', this.updateControls, { passive: true, signal: this.events.signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-recommendation-direction]');
        if (!button) return;
        const { step, perPage } = this.metrics();
        this.grid.scrollBy({ left: Number(button.dataset.recommendationDirection) * step * perPage, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, { signal: this.events.signal });
      this.resizeObserver = new ResizeObserver(this.updateControls);
      this.resizeObserver.observe(this.grid);
      this.updateControls();
      const cartSection = document.querySelector('[data-section-type="cart-template"]');
      if (cartSection) this.cartObserver.observe(cartSection, { childList: true, subtree: true });
      window.addEventListener('pageshow', this.scheduleRefresh);
      this.refresh();
    }

    disconnectedCallback() {
      clearTimeout(this.refreshTimer);
      this.cartObserver?.disconnect();
      this.controller?.abort();
      this.events?.abort();
      this.resizeObserver?.disconnect();
      window.removeEventListener('pageshow', this.scheduleRefresh);
    }

    metrics() {
      const gap = parseFloat(getComputedStyle(this.grid).columnGap) || 0;
      const step = Math.max(1, (this.grid.firstElementChild?.getBoundingClientRect().width || this.grid.clientWidth) + gap);
      return { step, perPage: Math.max(1, Math.floor((this.grid.clientWidth + gap + 1) / step)) };
    }

    updateControls() {
      const controls = this.querySelector('[data-recommendation-controls]');
      if (!controls) return;
      const count = this.grid.children.length;
      const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
      controls.hidden = count < 2 || maxScroll < 2;
      const { step, perPage } = this.metrics();
      const start = Math.min(count, Math.round(this.grid.scrollLeft / step) + 1);
      controls.querySelector('[data-recommendation-position]').textContent = `${start}–${Math.min(count, start + perPage - 1)} / ${count}`;
      controls.querySelector('[data-recommendation-direction="-1"]').disabled = this.grid.scrollLeft <= 2;
      controls.querySelector('[data-recommendation-direction="1"]').disabled = this.grid.scrollLeft >= maxScroll - 2;
    }

    async refresh() {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      try {
        const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, { signal: controller.signal });
        if (!cartResponse.ok) return;
        const cart = await cartResponse.json();
        const signature = JSON.stringify(cart.items.map(item => item.product_id));
        if (signature === this.signature) return;
        const currentIds = new Set(cart.items.map(item => String(item.product_id)));
        this.querySelectorAll('[data-recommended-product]').forEach(card => {
          if (currentIds.has(card.dataset.recommendedProduct)) card.remove();
        });
        this.hidden = !this.querySelector('[data-recommended-product]');
        this.updateControls();
        const url = new URL(cart.items.length ? this.dataset.recommendationsUrl : this.dataset.cartUrl, location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        if (cart.items.length) {
          url.searchParams.set('product_id', cart.items[0].product_id);
          url.searchParams.set('intent', 'related');
          url.searchParams.set('limit', '10');
        }
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) return;
        const html = await response.text();
        if (controller.signal.aborted || !this.isConnected) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const grid = documentFragment.querySelector('[data-cart-recommendation-grid]');
        if (!grid) return;
        const seen = new Set(currentIds);
        grid.querySelectorAll('[data-recommended-product]').forEach(card => {
          if (seen.has(card.dataset.recommendedProduct)) card.remove();
          else seen.add(card.dataset.recommendedProduct);
        });
        this.querySelector('[data-cart-recommendation-grid]').replaceChildren(...grid.children);
        const browse = this.querySelector('.cart-recommendations__browse');
        const nextBrowse = documentFragment.querySelector('.cart-recommendations__browse');
        if (browse && nextBrowse) browse.replaceWith(nextBrowse);
        else if (browse) browse.remove();
        else if (nextBrowse) this.append(nextBrowse);
        this.hidden = !this.querySelector('[data-recommended-product]');
        this.grid.scrollLeft = 0;
        this.updateControls();
        this.signature = signature;
      } catch (error) {
        if (error.name !== 'AbortError') this.dataset.recommendationStatus = 'fallback';
      }
    }
  });
}
